import { useLayoutEffect } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import type { AuthRedirectTarget } from '../../../../routing';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A small hook that redirects the user to the new "Join or Sign In" screen if
 * the corresponding feature flag is enabled.
 *
 * Can be used on legacy authentication routes.
 */
export const useAzureAuthScreenRedirect = () => {
  const router = useRouter();
  const params = useLocalSearchParams<AuthRouteParams>();
  const { track } = useTelemetry();

  const isHybridAuthFlowEnabled = useFeatureFlag('cels-2041-auth-hybrid-flow', {
    listenForChanges: true,
  });

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    const routeParams = params ?? ROUTE_PARAMS_FALLBACK;

    if (isHybridAuthFlowEnabled) {
      // NOTE: We track the "view" event here since `JoinOrSignScreen` is also used as a redirect target
      //       when employing authentication "Redirect" flow, which can result in additional telemetry events.
      track('join-or-sign-in.view', { referrer: routeParams.redirect });
    }

    router.replace({
      pathname: '/(auth)/join-or-sign-in',
      params: routeParams,
    });
  }, [router, params, track, isHybridAuthFlowEnabled]);
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ROUTE_PARAMS_FALLBACK: AuthRouteParams = {};

// ─── Types ───────────────────────────────────────────────────────────────────

type AuthRouteParams = {
  redirect?: AuthRedirectTarget;
};
